class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.freeShippingThreshold = 500;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initUpsellEventListeners();
    this.fetchCartAndUpdateProgress();

    // Quantity +/- and remove inside the drawer go through CartItems.updateQuantity
    // (assets/cart.js), which re-renders `.drawer__inner` from the server's static
    // Liquid markup - resetting the progress bar to its unqualified placeholder text
    // with no recalculation. That path never calls our JS, only cart-update pub/sub
    // fires from there, so recompute from that event for the cart-items source only
    // (product-form add-to-cart already recomputes directly in renderContents).
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        this.fetchCartAndUpdateProgress();
        if (this.lastAddedProductId) {
          this.fetchUpsell(this.lastAddedProductId);
        }
      }
    });

    // Root cause of "cart looks unchanged after navigating away and back":
    // mobile browsers can show a page again (via the back-forward cache,
    // or their own cached render of a history entry) without the cart-icon
    // bubble or #CartDrawer ever re-running the JS that would notice the
    // cart changed in the meantime (e.g. an add-to-cart on the page the user
    // briefly visited). `pageshow`'s `event.persisted` flag is the textbook
    // signal for a bfcache restore, but testing showed at least one mobile
    // scenario where the visible page is stale on back-navigation WITHOUT
    // persisted ever being true (this restores a rendered snapshot without
    // truly freezing/resuming the JS realm, so persisted-gated refreshes
    // never fire) - so refresh on every pageshow, not just persisted ones,
    // and again on visibilitychange for the "switched app tab and came
    // back" case. This is just a cheap background section refetch each
    // time, not a behavior change for the common case where nothing's stale.
    window.addEventListener('pageshow', () => {
      this.refreshFromBfcacheRestore();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.refreshFromBfcacheRestore();
      }
    });
  }

  refreshFromBfcacheRestore() {
    fetch(`${window.Shopify.routes.root}?sections=cart-drawer,cart-icon-bubble`, { cache: 'no-store' })
      .then((response) => response.json())
      .then((sections) => {
        if (sections['cart-drawer']) {
          const newDrawer = this.getSectionDOM(sections['cart-drawer'], '#CartDrawer');
          const oldDrawer = document.getElementById('CartDrawer');
          if (newDrawer && oldDrawer) oldDrawer.innerHTML = newDrawer.innerHTML;
        }
        if (sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          const newBubble = this.getSectionDOM(sections['cart-icon-bubble'], '.shopify-section');
          if (bubble && newBubble) bubble.innerHTML = newBubble.innerHTML;
        }
        this.updateProgressFromDOM();
        // This replace above is a wholesale swap from a fresh, JS-untouched
        // section render, so #cart-drawer-upsell comes back empty every time
        // (same reason renderContents() has to re-populate it after its own
        // section replace). scheduleReconcile() calls this method 700ms
        // after every add specifically to fix the request-race bug - but
        // without this line, that same safety net was wiping out the upsell
        // picks that had just finished loading, making the blue upsell
        // section disappear shortly after the very first add to an empty
        // cart (quantity-change never hits this path, which is why that
        // scenario didn't show the bug).
        if (this.lastAddedProductId) {
          this.fetchUpsell(this.lastAddedProductId);
        }
      })
      .catch((e) => {
        console.error('Error refreshing cart after bfcache restore:', e);
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.hideThirdPartyOverlays();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.restoreThirdPartyOverlays();
  }

  // Klaviyo's onsite embed wraps its teasers/popups in a fixed, full-viewport
  // div carrying its own "kl-private-reset-css-*" class, at z-index 90000 -
  // high enough to sit above our drawer (z-index 1000) but far below what we
  // assumed. Match on that class (Klaviyo's own signature) as well as a
  // generic high-z-index fallback, hide while the drawer is open, restore on
  // close.
  hideThirdPartyOverlays() {
    this._hiddenOverlays = Array.from(document.body.querySelectorAll('*')).filter((el) => {
      if (this.contains(el)) return false;
      if (Array.from(el.classList).some((c) => c.startsWith('kl-private-reset-css'))) return true;
      const cs = getComputedStyle(el);
      return (cs.position === 'fixed' || cs.position === 'sticky') && parseInt(cs.zIndex, 10) > 10000;
    });
    this._hiddenOverlays.forEach((el) => {
      el.dataset.crodrawerHiddenVisibility = el.style.visibility || '';
      el.style.visibility = 'hidden';
    });
  }

  restoreThirdPartyOverlays() {
    (this._hiddenOverlays || []).forEach((el) => {
      el.style.visibility = el.dataset.crodrawerHiddenVisibility || '';
      delete el.dataset.crodrawerHiddenVisibility;
    });
    this._hiddenOverlays = [];
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // The "is-empty" class that switches .drawer__inner into the centered
    // empty-cart grid layout (component-cart-drawer.css) lives on <cart-drawer>
    // itself (this), not on .drawer__inner - clearing it from the wrong element
    // left first-ever add-to-cart (starting from an empty cart) stuck in the
    // empty-cart layout, which visually swallowed the items table.
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.lastAddedProductId = parsedState.product_id;

    // parsedState.sections is occasionally missing/malformed - e.g. when
    // Shopify's bot-verification interstitial intercepts the add.js request
    // and returns its challenge page instead of the expected JSON. That used
    // to throw here (reading parsedState.sections[id] on undefined) and abort
    // the whole function, silently skipping the this.open() call below - the
    // item was added server-side (cart badge updates) but the drawer never
    // opened and the progress bar never refreshed, with no visible error.
    if (parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
    } else {
      console.error('cart-drawer: renderContents got a response with no sections; skipping section refresh', parsedState);
    }

    // The section re-render above replaces #CartDrawer's innerHTML wholesale,
    // so the free-shipping bar and upsell picks need repopulating every time.
    // Read the total from the section HTML we just injected (rendered
    // server-side by the SAME add.js request, so it's guaranteed to already
    // reflect this add) instead of firing a separate /cart.js GET here - that
    // extra request could reach Shopify before this add's cart-session write
    // had fully committed, occasionally coming back with the PRE-add total
    // and leaving the progress bar stuck showing "still need $500" on carts
    // that already qualified (e.g. adding a 5-pack/10-pack bundle variant).
    this.updateProgressFromDOM();
    if (this.lastAddedProductId) {
      this.fetchUpsell(this.lastAddedProductId);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });

    // Rapid-fire adds (e.g. tapping 4 different quick-add buttons in a row)
    // each fire their own independent /cart/add.js request and each one's
    // response, whenever it happens to arrive, replaces #CartDrawer wholesale
    // with whatever THAT request's sections snapshot was. If responses come
    // back out of arrival order (ordinary on real mobile networks under
    // parallel requests), the chronologically-last DOM replace can be built
    // from an EARLIER, incomplete snapshot - permanently showing fewer items
    // than are actually in the cart, with no further trigger to correct it.
    // scheduleReconcile() is the safety net: after a short quiet period with
    // no further add, re-fetch sections fresh and re-apply, so the very last
    // thing that happens is always a read of the true, current cart state.
    this.scheduleReconcile();
  }

  scheduleReconcile() {
    clearTimeout(this._reconcileTimer);
    this._reconcileTimer = setTimeout(() => {
      this.refreshFromBfcacheRestore();
    }, 700);
  }

  // Reads the cart total already rendered into #cart-drawer-progress's
  // data-cart-total-cents attribute (see snippets/cart-drawer.liquid) instead
  // of re-fetching /cart.js, so callers that just replaced this element's
  // HTML from an authoritative response can't race a stale cart-session read.
  updateProgressFromDOM() {
    const progressContainer = document.getElementById('cart-drawer-progress');
    if (!progressContainer) return;
    const totalCents = Number(progressContainer.dataset.cartTotalCents || 0);
    this.updateProgressBar({ total_price: totalCents });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  fetchCartAndUpdateProgress() {
    // no-store: mobile browsers (Safari, in-app webviews) can silently serve a
    // cached GET response here, showing a stale total/progress-bar state that
    // doesn't reflect an item just added moments earlier.
    fetch(`${window.Shopify.routes.root}cart.js`, { cache: 'no-store' })
      .then((response) => response.json())
      .then((cart) => {
        this.updateProgressBar(cart);
      })
      .catch((e) => {
        console.error('Error fetching cart for progress bar:', e);
      });
  }

  updateProgressBar(cartData) {
    const progressContainer = document.getElementById('cart-drawer-progress');
    if (!progressContainer) return;

    // cart.total_price is in cents, convert to dollars
    const cartTotal = cartData.total_price ? cartData.total_price / 100 : 0;
    const amountRemaining = this.freeShippingThreshold - cartTotal;
    const progressPercent = Math.min((cartTotal / this.freeShippingThreshold) * 100, 100);

    const progressFill = document.getElementById('cart-drawer-progress-fill');
    const progressBar = progressContainer.querySelector('.cart-notification-progress__bar');
    if (progressFill) {
      progressFill.style.width = `${progressPercent}%`;
    }

    const progressText = progressContainer.querySelector('.cart-notification-progress__text');
    if (progressText) {
      if (amountRemaining > 0) {
        progressContainer.classList.remove('cart-notification-progress--complete');
        progressText.innerHTML = `
          <span class="cart-notification-progress__message">
            🔥 差 <span class="cart-notification-progress__amount">$${Math.ceil(amountRemaining)}</span> 即享免運，順豐直送・<span style="white-space:nowrap;">立即加購</span>
          </span>
        `;
      } else {
        progressContainer.classList.add('cart-notification-progress--complete');
        progressText.innerHTML = `
          <span class="cart-notification-progress__message cart-notification-progress__message--complete">
            <svg class="cart-notification-progress__check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7" cy="7" r="7" fill="#22D06B"/>
              <path d="M4 7L6 9L10 5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            已享免運費！
          </span>
        `;
      }
    }

    if (progressBar) progressBar.style.display = 'block';

    const upsellContainer = document.getElementById('cart-drawer-upsell');
    if (upsellContainer) {
      if (amountRemaining <= 0) {
        upsellContainer.dataset.showUpsell = 'false';
        upsellContainer.innerHTML = '';
        upsellContainer.style.display = 'none';
      } else {
        upsellContainer.dataset.showUpsell = 'true';
        upsellContainer.style.display = 'block';

        // Opening the drawer via the header cart icon (or on page load with
        // items already in the cart) never goes through renderContents/the
        // cart-update handler, so fetchUpsell() never runs and this section
        // stays permanently blank. Populate it here from the cart's own data
        // any time it's supposed to show but hasn't been filled in yet.
        if (!upsellContainer.innerHTML.trim() && cartData.items && cartData.items.length) {
          this.fetchUpsell(cartData.items[cartData.items.length - 1].product_id);
        }
      }
    }
  }

  fetchUpsell(productId) {
    const upsellContainer = document.getElementById('cart-drawer-upsell');
    if (!upsellContainer || upsellContainer.dataset.showUpsell === 'false') return;

    // On a slow connection, this fetch can still be in flight when a later
    // event (scheduleReconcile's 700ms reconcile, another add, etc.) wholesale
    // -replaces #CartDrawer's innerHTML - the `upsellContainer` captured above
    // would then be a detached node, no longer part of the visible document.
    // Writing into it "succeeds" with no error but produces nothing on screen.
    // On top of that, two overlapping fetchUpsell calls (e.g. the immediate
    // one from renderContents() plus the reconcile's repopulate call) can
    // resolve out of order, so the request that finishes last isn't
    // necessarily the most recent one. Fix both by re-fetching the container
    // fresh from the DOM at write time, and by tagging each call with a
    // monotonic token so a stale, slow response can't overwrite content a
    // newer call already rendered.
    const requestToken = (this._upsellRequestToken = (this._upsellRequestToken || 0) + 1);
    const url = `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=100&intent=complementary&section_id=cart-upsell`;

    fetch(url, { cache: 'no-store' })
      .then((response) => response.text())
      .then((text) => {
        if (requestToken !== this._upsellRequestToken) return;

        const freshContainer = document.getElementById('cart-drawer-upsell');
        if (!freshContainer) return;

        const html = document.createElement('div');
        html.innerHTML = text;

        const recommendations = html.querySelector('.cart-upsell');
        if (recommendations) {
          freshContainer.innerHTML = recommendations.outerHTML;
          this.initUpsellEventListeners();
        }
      })
      .catch((e) => {
        console.error('Error fetching upsell recommendations:', e);
      });
  }

  initUpsellEventListeners() {
    const upsellContainer = document.getElementById('cart-drawer-upsell');
    if (!upsellContainer) return;

    upsellContainer.querySelectorAll('.cart-upsell-item__qty-btn').forEach((btn) => {
      btn.addEventListener('click', this.handleQuantityChange.bind(this));
    });

    upsellContainer.querySelectorAll('.cart-upsell-item__add-btn').forEach((btn) => {
      btn.addEventListener('click', this.handleUpsellAddToCart.bind(this));
    });

    this.initCustomScrollbar(upsellContainer);
  }

  initCustomScrollbar(container) {
    const productsList = container.querySelector('.cart-upsell__products');
    const scrollTrack = container.querySelector('.cart-upsell__scrollbar-track');
    const scrollThumb = container.querySelector('.cart-upsell__scrollbar-thumb');

    if (!productsList || !scrollTrack || !scrollThumb) return;

    const updateScrollbar = () => {
      const scrollHeight = productsList.scrollHeight;
      const clientHeight = productsList.clientHeight;
      const scrollTop = productsList.scrollTop;

      if (scrollHeight <= clientHeight || clientHeight === 0) {
        scrollTrack.classList.add('hidden');
        scrollThumb.classList.add('hidden');
        return;
      }

      scrollTrack.classList.remove('hidden');
      scrollThumb.classList.remove('hidden');

      const thumbHeightPercent = (clientHeight / scrollHeight) * 100;
      const thumbHeight = Math.max(thumbHeightPercent, 20);
      scrollThumb.style.height = `${thumbHeight}%`;

      const maxScrollTop = scrollHeight - clientHeight;
      const scrollPercent = maxScrollTop > 0 ? scrollTop / maxScrollTop : 0;
      const maxThumbTop = 100 - thumbHeight;
      const thumbTop = scrollPercent * maxThumbTop;
      scrollThumb.style.top = `${thumbTop}%`;
    };

    const initWithDelay = () => {
      updateScrollbar();
      setTimeout(updateScrollbar, 50);
      setTimeout(updateScrollbar, 150);
      setTimeout(updateScrollbar, 500);
    };

    initWithDelay();

    productsList.addEventListener('scroll', updateScrollbar, { passive: true });
    window.addEventListener('resize', updateScrollbar, { passive: true });
  }

  handleQuantityChange(event) {
    const button = event.currentTarget;
    const item = button.closest('.cart-upsell-item');
    const qtyDisplay = item.querySelector('.cart-upsell-item__qty-value');
    let qty = parseInt(qtyDisplay.textContent) || 1;

    if (button.name === 'plus') {
      qty += 1;
    } else if (button.name === 'minus' && qty > 1) {
      qty -= 1;
    }

    qtyDisplay.textContent = qty;
  }

  handleUpsellAddToCart(event) {
    const button = event.currentTarget;
    const item = button.closest('.cart-upsell-item');
    const variantId = button.dataset.variantId;
    const productId = item.dataset.productId;
    const qtyDisplay = item.querySelector('.cart-upsell-item__qty-value');
    const quantity = parseInt(qtyDisplay.textContent) || 1;

    if (!variantId) return;

    this.showLoading();

    const formData = {
      items: [
        {
          id: parseInt(variantId),
          quantity: quantity,
        },
      ],
    };

    fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error('Error adding to cart:', response.description);
          this.hideLoading();
          return;
        }

        this.lastAddedProductId = productId;
        this.refreshCartDrawerFull(response);
      })
      .catch((e) => {
        console.error('Error adding to cart:', e);
        this.hideLoading();
      });
  }

  showLoading() {
    const loadingOverlay = document.getElementById('cart-drawer-loading');
    if (loadingOverlay) {
      loadingOverlay.classList.add('active');
      loadingOverlay.setAttribute('aria-hidden', 'false');
    }
  }

  hideLoading() {
    const loadingOverlay = document.getElementById('cart-drawer-loading');
    if (loadingOverlay) {
      loadingOverlay.classList.remove('active');
      loadingOverlay.setAttribute('aria-hidden', 'true');
    }
  }

  refreshCartDrawerFull(addedItemResponse) {
    this.classList.remove('is-empty');
    fetch(`${window.Shopify.routes.root}?sections=cart-drawer,cart-icon-bubble`, { cache: 'no-store' })
      .then((response) => response.json())
      .then((sections) => {
        if (sections['cart-drawer']) {
          const newDrawer = this.getSectionDOM(sections['cart-drawer'], '#CartDrawer');
          const oldDrawer = document.getElementById('CartDrawer');
          if (newDrawer && oldDrawer) oldDrawer.innerHTML = newDrawer.innerHTML;
        }
        if (sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          const newBubble = this.getSectionDOM(sections['cart-icon-bubble'], '.shopify-section');
          if (bubble && newBubble) bubble.innerHTML = newBubble.innerHTML;
        }

        if (addedItemResponse.product_id) {
          this.lastAddedProductId = addedItemResponse.product_id;
          this.fetchUpsell(this.lastAddedProductId);
        }
        // Read from the sections response just injected above rather than
        // firing yet another /cart.js GET - see updateProgressFromDOM().
        this.updateProgressFromDOM();
        this.hideLoading();
        // Same out-of-order-response race as renderContents() - see
        // scheduleReconcile()'s comment there.
        this.scheduleReconcile();
      })
      .catch((e) => {
        console.error('Error refreshing cart drawer:', e);
        this.hideLoading();
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // CartItems.connectedCallback() (assets/cart.js) subscribes to cart-update
  // and calls onCartUpdate() for every source except 'cart-items' - which
  // means the 'product-form' event that PDP add-to-cart publishes ALSO
  // triggers it here. onCartUpdate() does its own independent
  // `fetch(routes.cart_url + '?section_id=cart-drawer')` and swaps in
  // cart-drawer-items wholesale from that raw, JS-untouched response - firing
  // moments after CartDrawer.renderContents() (assets/cart-drawer.js) already
  // handled the exact same add correctly, it silently stomped the just-fixed
  // progress bar back to its unqualified placeholder text/0% fill (data-
  // cart-total-cents stayed correct, since that's baked into the fresh HTML
  // too, but nothing re-ran the JS that turns it into the real message).
  // CartDrawer.renderContents() already owns this update end-to-end, so skip
  // the redundant refresh entirely instead of just papering over the race.
  onCartUpdate() {}
}

customElements.define('cart-drawer-items', CartDrawerItems);
